/* Additional Real Estate Pro interface translations (NL / EN / FR / ES) for the
   authenticated customer app, checkout pages, legal chrome and legal SEO.
   Deep-merged into the main DICT in dictionaries.js. Interface text only. */
import { LEGAL } from "./legalContent";

/* ----------------------------- DUTCH ----------------------------- */
const nl = {
  common: { replay: "Video opnieuw afspelen", language: "Taal" },
  nav: { home: "Homepage", ariaHome: "Real Estate Pro — naar de homepagina", ariaMenuOpen: "Menu openen", ariaMenuClose: "Menu sluiten" },
  auth: {
    register: {
      errShort: "Het wachtwoord moet minstens 8 tekens bevatten.",
      errMismatch: "De wachtwoorden komen niet overeen.",
      errAccept: "Ga akkoord met de Algemene Voorwaarden en het Privacybeleid.",
      confirm: "Bevestig wachtwoord",
    },
    changePassword: { short: "Het nieuwe wachtwoord moet minstens 8 tekens bevatten." },
  },
  app: {
    checkout: {
      readyTitle: "Je account is klaar",
      activatedPlan: "Geactiveerd pakket",
      availableCredits: "Beschikbare projectcredits",
      createFirst: "Maak je eerste project",
      toDashboard: "Naar dashboard",
      almostThere: "Bijna klaar",
      confirming: "Je betaling wordt bevestigd…",
      slowText: "Je betaling is ontvangen en wordt nog bevestigd. Deze pagina wordt automatisch bijgewerkt.",
      confirmingText: "Even geduld terwijl we je betaling bevestigen en je projectcredits activeren.",
      cancelTitle: "Afrekenen geannuleerd",
      cancelText: "Er is geen betaling uitgevoerd en er is niets gewijzigd op je account. Je kunt een pakket kiezen en het opnieuw proberen wanneer je klaar bent.",
      tryAgain: "Opnieuw proberen",
      backToDashboard: "Terug naar dashboard",
    },
  },
  dash: {
    welcomeTitle: "Maak binnen enkele minuten vastgoedvideo's",
    welcomeSub: "Upload je listingfoto's en ontvang zes kant-en-klare vastgoedvideo's.",
    newProject: "Nieuw project",
    creditsLabel: "Beschikbare credits",
    projectCredits: "projectcredits",
    complimentary: "Gratis credit — probeer Real Estate Pro op onze kosten",
    creditExplainer: "Met één projectcredit maak je zes video's in één gekozen formaat.",
    attn: {
      past_due: { title: "Betaling vereist aandacht", text: "We konden je abonnementsbetaling niet verwerken, dus er zijn geen nieuwe maandelijkse credits toegevoegd. Projectcredits die al in je saldo staan, blijven beschikbaar. Werk je betaalmethode bij om je maandelijkse credits te hervatten.", cta: "Betaalmethode bijwerken" },
      unpaid: { title: "Abonnement onbetaald", text: "Je abonnement is niet meer actief omdat de betaling niet kon worden voltooid. Projectcredits die al in je saldo staan, blijven beschikbaar. Beheer je facturatie om maandelijkse credits te hervatten.", cta: "Facturatie beheren" },
      incomplete: { title: "Abonnementsinstallatie onvolledig", text: "Je abonnementsbetaling is nog niet voltooid. Voltooi de betaling om je pakket en maandelijkse credits te activeren.", cta: "Betaling voltooien" },
      incomplete_expired: { title: "Abonnement niet geactiveerd", text: "De abonnementsinstallatie is niet voltooid. Kies een pakket om het opnieuw te proberen. Je account, projecten en bestaande credits blijven beschikbaar.", cta: "Bekijk pakketten" },
      paused: { title: "Abonnement gepauzeerd", text: "Je abonnement is momenteel gepauzeerd. Projectcredits die al in je saldo staan, blijven beschikbaar. Beheer je facturatie om maandelijkse credits te hervatten.", cta: "Facturatie beheren" },
    },
    viewBilling: "Bekijk facturatie",
    cancelTitle: "Opzegging gepland",
    cancelText: "Je abonnement eindigt op {date}. Tot dan kun je Real Estate Pro blijven gebruiken.",
    manageSubscription: "Abonnement beheren",
    endedTitle: "Je abonnement is beëindigd",
    endedText: "Je hebt nog {n} betaalde project{cw} beschikbaar. Je kunt met deze credits projecten blijven maken. Kies een nieuw pakket wanneer je weer maandelijkse credits wilt ontvangen.",
    cw1: "credit", cwN: "credits",
    viewPlans: "Bekijk pakketten",
    zeroTitleMore: "Klaar om meer te maken?",
    zeroTitleStart: "Kies een pakket om video's te gaan maken",
    zeroTextEnded: "Kies een pakket om nieuwe projectcredits te ontvangen — je account, projecten en video's blijven gewoon hier staan.",
    zeroTextDemo: "Je hebt je gratis credit gebruikt. Kies een pakket om nieuwe projectcredits te ontvangen — je account, projecten en video's blijven gewoon hier staan.",
    zeroTextNew: "Selecteer een Real Estate Pro-pakket om projectcredits voor je account te activeren.",
    projectsHeading: "Projecten",
    filters: { all: "Alles", CONCEPT: "Concepten", PROCESSING: "In verwerking", COMPLETED: "Afgerond", SUPPORT_REVIEW: "In controle" },
    states: { CONCEPT: "Concept", PROCESSING: "Bezig met genereren", COMPLETED: "Video's klaar", SUPPORT_REVIEW: "In controle" },
    noProjects: "Nog geen projecten",
    emptyCanCreate: "Maak je eerste project en zet je vastgoedfoto's om in zes afgewerkte video's.",
    emptyNoCredits: "Kies hierboven een pakket om je credits te activeren en video's te gaan maken.",
    createFirst: "Eerste project aanmaken",
    filterEmpty: "Geen projecten in deze categorie.",
    processingHint: "Meestal klaar binnen 1–5 minuten.",
    openProject: "Project openen",
  },
  np: {
    stages: ["Project", "Foto's", "Genereren"],
    nameLabel: "Projectnaam",
    namePlaceholder: "Bijvoorbeeld: Appartement in Gent, Villa Knokke of Project 2026-08",
    formatLabel: "Formaat",
    portrait: "Verticaal", portraitDesc: "1080 × 1920 · Ideaal voor Instagram, TikTok en Reels",
    landscape: "Horizontaal", landscapeDesc: "1920 × 1080 · Ideaal voor websites, YouTube en presentaties",
    ratioNote: "Verticaal en horizontaal zijn aparte projecten en gebruiken elk één projectcredit.",
    photosTitle: "Upload je vastgoedfoto's",
    photosSub: "Voeg tot 20 foto's toe en plaats ze in de gewenste volgorde.",
    dropText: "Klik of sleep foto's hierheen",
    dropHint: "JPEG, PNG of WebP · max 20 MB · tot {max} foto's",
    selected: "{n} / {max} foto's geselecteerd",
    photoAlt: "Foto {n}",
    replaceTitle: "Vervangen",
    summaryPhotos: "Foto's",
    summaryResult: "Resultaat",
    resultValue: "6 video's · MP4",
    resultHint: "Zes afgewerkte video's · MP4 · 30 FPS · {res}.",
    noCredits: "Je hebt geen projectcredits beschikbaar.",
    choosePackage: "Kies een pakket",
    generate: "Video's genereren",
    continueBtn: "Doorgaan",
    back: "Terug",
    confirmTitle: "Dit project genereren?",
    confirmText: "Dit gebruikt 1 projectcredit en maakt zes video's in het gekozen formaat.",
    confirmGenerate: "Bevestigen en genereren",
    toastMax: "Maximum van {max} foto's bereikt",
    toastType: "{name}: niet-ondersteund bestandstype",
    toastSize: "{name}: groter dan 20 MB",
    toastInvalid: "Ongeldig bestand.",
    preparing: "Project voorbereiden…",
    uploading: "Foto {i} van {n} uploaden",
    starting: "Videogeneratie starten…",
    started: "Project gestart. Je video's worden gemaakt.",
    uploadFailed: "Foto {i} kon niet worden geüpload. Je credit is niet gebruikt. {msg}",
  },
  bill: {
    title: "Pakketten & credits",
    sub: "Kies het pakket dat het beste bij je kantoor past en beheer je projectcredits.",
    notices: {
      past_due: { title: "Betaling vereist aandacht", text: "We konden je abonnementsbetaling niet verwerken. Werk je betaalmethode bij om nieuwe projecten te blijven maken.", portal: "Betaalmethode bijwerken" },
      unpaid: { title: "Abonnement onbetaald", text: "Je abonnement is niet meer actief omdat de betaling niet kon worden voltooid. Kies hieronder een pakket om opnieuw te starten.", portal: "Facturatie beheren" },
      incomplete: { title: "Abonnementsinstallatie onvolledig", text: "Je abonnementsbetaling is nog niet voltooid.", portal: "Betaling voltooien" },
      incomplete_expired: { title: "Abonnement niet geactiveerd", text: "De abonnementsinstallatie is niet voltooid. Kies hieronder een pakket om het opnieuw te proberen." },
      paused: { title: "Abonnement gepauzeerd", text: "Je abonnement is momenteel gepauzeerd.", portal: "Facturatie beheren" },
      cancelled: { title: "Abonnement beëindigd", text: "Je abonnement is niet meer actief. Kies hieronder een pakket om opnieuw te activeren — je bestaande projecten en video's blijven beschikbaar." },
    },
    openingPortal: "Facturatieportaal openen…",
    creditsLabel: "Beschikbare credits",
    projectCredits: "projectcredits",
    complimentary: "Gratis credit — probeer Real Estate Pro op onze kosten",
    creditExplainer: "Met één projectcredit maak je zes video's in één gekozen formaat.",
    currentPlan: "Huidig pakket",
    pendingPlan: "Pakket in behandeling",
    noPlan: "Geen actief pakket",
    creditsAfterPayment: "Je credits worden pas toegevoegd nadat de betaling is bevestigd.",
    active: "Actief",
    monthlyPrice: "Maandprijs",
    perMonthVat: "/ maand, excl. btw",
    monthlyCredits: "Maandelijkse projectcredits",
    nextBilling: "Volgende facturatiedatum",
    billingInfo: "Facturatiegegevens",
    vatTaxId: "Btw-nummer:",
    cancelScheduled: "Je abonnement eindigt op {date}. Je behoudt tot dan volledige toegang en je kunt het op elk moment hervatten via Abonnement beheren.",
    manageSubscription: "Abonnement beheren",
    viewWebsite: "Bekijk website",
    backToDashboard: "Terug naar dashboard",
    otherPlans: "Andere pakketten",
    chooseYourPlan: "Kies je pakket",
    switchHelpAdmin: "Je kunt hieronder van pakket wisselen — wijzigingen gaan **onmiddellijk** in en je bestaande projectcredits blijven altijd behouden. Om op te zeggen of betaalgegevens bij te werken, gebruik je hierboven **Abonnement beheren**.",
    switchHelpUser: "Alleen een beheerder van het kantoor kan het pakket wijzigen. Om op te zeggen of betaalgegevens bij te werken, kan een beheerder hierboven **Abonnement beheren** gebruiken.",
    viewOnly: "Alleen een beheerder van het kantoor kan een abonnement starten of beheren.",
    acceptTerms: "Ik ga akkoord met de {l:terms} en neem kennis van het {l:privacy}.",
    confirmBusiness: "Ik bevestig dat ik Real Estate Pro aankoop voor professionele of zakelijke doeleinden.",
    openingCheckout: "Veilig afrekenen openen…",
    switchTo: "Wijzig naar {name}",
    choose: "Kies {name}",
    switchTitle: "Wijzig naar {name}",
    switchBody1: "Je wijzigt van **{current}** naar **{target}**. Je {target}-abonnement start onmiddellijk.",
    switchBody2: "Je betaalt nu **€{price} excl. btw**, plus eventuele toepasselijke belasting.",
    switchBody3: "Je huidige ongebruikte betaalde credits blijven op je account staan. Nadat de nieuwe betaling is gelukt, worden **{credits} nieuwe projectcredits** aan je saldo toegevoegd.",
    switchBody4: "Je nieuwe {target}-abonnement wordt vanaf vandaag maandelijks verlengd. Resterende tijd in je huidige {current}-facturatieperiode wordt niet pro rata verrekend of terugbetaald.",
    switchResume: "Je abonnement is momenteel gepland voor opzegging. Door van pakket te wisselen **hervat** je je abonnement en start het nieuwe pakket onmiddellijk.",
    confirmPay: "Bevestigen en €{price} betalen",
    processing: "Verwerken…",
    successTitle: "Pakket succesvol gewijzigd",
    successText: "Je {plan}-pakket is nu actief. Er zijn {credits} nieuwe projectcredits aan je saldo toegevoegd. Je bestaande credits zijn behouden. Je hebt nu **{total} projectcredits**. Je volgende facturatiedatum is **{date}**.",
    processingTitle: "Je pakketwijziging wordt afgerond",
    processingText: "Je betaling wordt bevestigd. Je nieuwe pakket en credits verschijnen hier zodra de betaling is voltooid. Je bestaande credits blijven veilig.",
    errorTitle: "We konden je pakketwijziging niet voltooien",
    howTitle: "Hoe credits werken",
    howText: "Elke geslaagde maandelijkse abonnementsbetaling voegt de projectcredits van je pakket toe aan je saldo. Ongebruikte betaalde credits worden meegenomen en vervallen niet zolang je account open blijft en Real Estate Pro de dienst blijft aanbieden. Opzeggen stopt toekomstige maandelijkse credits, maar verwijdert geen betaalde credits die al in je saldo staan. Als je van pakket wisselt, blijven je bestaande credits behouden en wordt de nieuwe pakkettoewijzing toegevoegd na de volgende geslaagde verlengbetaling. Gratis credits volgen hun eigen aparte geldigheidsregels. Zie onze {l:terms|credit- en abonnementsvoorwaarden}.",
    futureNote: "Je wordt doorgestuurd naar de beveiligde checkout om je abonnement te voltooien. Prijzen worden getoond excl. btw; de toepasselijke belasting wordt bij het afrekenen berekend. Je credits worden pas toegevoegd nadat de betaling is bevestigd.",
    errAccept: "Ga akkoord met de Voorwaarden en bevestig je zakelijke aankoop om verder te gaan.",
    errActiveSub: "Je hebt al een actief abonnement. Gebruik Abonnement beheren om wijzigingen aan te brengen.",
    errCheckout: "We konden de beveiligde checkout nu niet openen. Probeer het over een ogenblik opnieuw.",
    errPortal: "We konden het facturatieportaal nu niet openen. Probeer het over een ogenblik opnieuw.",
    errSwitch409: "Er wordt al een pakketwijziging verwerkt. Wacht even en controleer je facturatiegegevens.",
    errSwitchFailed: "We konden je pakketwijziging niet voltooien. Er zijn geen nieuwe pakketcredits toegevoegd en je bestaande credits blijven veilig. Probeer het opnieuw of werk je betaalmethode bij.",
  },
  acct: {
    title: "Account", personal: "Persoonlijke gegevens", organisation: "Organisatie",
    email: "E-mail", role: "Rol", administrator: "Beheerder", user: "Gebruiker",
    billing: "Facturatie", password: "Wachtwoord",
  },
  support: { needHelp: "Hulp nodig of een vraag?", contact: "Contact met support" },
  legalUi: {
    back: "Terug naar website", updated: "Laatst bijgewerkt: augustus 2026",
    opBy: "Beheerd door", entNo: "Ondernemingsnummer", vat: "Btw",
  },
  titles: {
    login: "Inloggen", register: "Account aanmaken", changePassword: "Wachtwoord wijzigen",
    dashboard: "Dashboard", newProject: "Nieuw project", account: "Account",
    billing: "Pakketten & credits", checkoutCancel: "Afrekenen geannuleerd",
    checkoutSuccess: "Betaling bevestigen", project: "Project",
  },
  seo: {
    terms: { title: "Algemene Voorwaarden — Real Estate Pro", description: "Lees de algemene voorwaarden voor het gebruik van Real Estate Pro: abonnementen, credits, betalingen en gebruiksregels." },
    privacy: { title: "Privacybeleid — Real Estate Pro", description: "Hoe Real Estate Pro persoonsgegevens verwerkt: welke data, doeleinden, rechtsgronden en je rechten." },
    cookies: { title: "Cookiebeleid — Real Estate Pro", description: "Real Estate Pro gebruikt alleen strikt noodzakelijke cookies voor authenticatie en beveiliging. Lees het cookiebeleid." },
    legal: { title: "Wettelijke kennisgeving — Real Estate Pro", description: "Bedrijfs- en contactgegevens van Real Estate Pro." },
    dpa: { title: "Verwerkersovereenkomst — Real Estate Pro", description: "De verwerkersovereenkomst tussen Real Estate Pro en zakelijke klanten voor persoonsgegevens." },
  },
  legal: LEGAL.nl,
};

/* ----------------------------- ENGLISH ---------------------------- */
const en = {
  common: { replay: "Replay video", language: "Language" },
  nav: { home: "Homepage", ariaHome: "Real Estate Pro — to the homepage", ariaMenuOpen: "Open menu", ariaMenuClose: "Close menu" },
  auth: {
    register: {
      errShort: "Password must be at least 8 characters.",
      errMismatch: "Passwords do not match.",
      errAccept: "Please accept the Terms and Privacy Policy.",
      confirm: "Confirm password",
    },
    changePassword: { short: "New password must be at least 8 characters." },
  },
  app: {
    checkout: {
      readyTitle: "Your account is ready",
      activatedPlan: "Activated plan",
      availableCredits: "Available project credits",
      createFirst: "Create your first project",
      toDashboard: "Go to dashboard",
      almostThere: "Almost there",
      confirming: "Confirming your payment…",
      slowText: "Your payment was received and is still being confirmed. This page will update automatically.",
      confirmingText: "Please wait a moment while we confirm your payment and activate your project credits.",
      cancelTitle: "Checkout cancelled",
      cancelText: "No payment was taken and nothing has changed on your account. You can pick a plan and try again whenever you're ready.",
      tryAgain: "Try again",
      backToDashboard: "Back to dashboard",
    },
  },
  dash: {
    welcomeTitle: "Create property videos in minutes",
    welcomeSub: "Upload your listing photos and receive six ready-to-use property videos.",
    newProject: "New project",
    creditsLabel: "Available credits",
    projectCredits: "project credits",
    complimentary: "Complimentary credit — try Real Estate Pro on us",
    creditExplainer: "One project credit creates six videos in one selected format.",
    attn: {
      past_due: { title: "Payment needs attention", text: "We couldn't process your subscription payment, so no new monthly credits were added. Any project credits already in your balance remain available to use. Update your payment method to resume your monthly credits.", cta: "Update payment method" },
      unpaid: { title: "Subscription unpaid", text: "Your subscription is no longer active because payment could not be completed. Any project credits already in your balance remain available to use. Manage your billing to resume monthly credits.", cta: "Manage billing" },
      incomplete: { title: "Subscription setup incomplete", text: "Your subscription payment has not been completed yet. Complete payment to activate your plan and monthly credits.", cta: "Complete payment" },
      incomplete_expired: { title: "Subscription not activated", text: "The subscription setup was not completed. Choose a plan to try again. Your account, projects and any existing credits remain available.", cta: "View plans" },
      paused: { title: "Subscription paused", text: "Your subscription is currently paused. Any project credits already in your balance remain available to use. Manage your billing to resume monthly credits.", cta: "Manage billing" },
    },
    viewBilling: "View billing",
    cancelTitle: "Cancellation scheduled",
    cancelText: "Your subscription will end on {date}. You can continue using Real Estate Pro until then.",
    manageSubscription: "Manage subscription",
    endedTitle: "Your subscription has ended",
    endedText: "You still have {n} paid project {cw} available. You can continue creating projects with these credits. Choose a new plan whenever you want to receive new monthly credits.",
    cw1: "credit", cwN: "credits",
    viewPlans: "View plans",
    zeroTitleMore: "Ready to create more?",
    zeroTitleStart: "Choose a plan to start creating videos",
    zeroTextEnded: "Choose a plan to receive new project credits — your account, projects and videos all stay right here.",
    zeroTextDemo: "You've used your complimentary credit. Choose a plan to receive new project credits — your account, projects and videos all stay right here.",
    zeroTextNew: "Select a Real Estate Pro plan to activate project credits for your account.",
    projectsHeading: "Projects",
    filters: { all: "All", CONCEPT: "Drafts", PROCESSING: "Processing", COMPLETED: "Completed", SUPPORT_REVIEW: "Under review" },
    states: { CONCEPT: "Draft", PROCESSING: "Generating", COMPLETED: "Videos ready", SUPPORT_REVIEW: "Under review" },
    noProjects: "No projects yet",
    emptyCanCreate: "Create your first project and turn your property photos into six finished videos.",
    emptyNoCredits: "Choose a plan above to activate your credits and start creating videos.",
    createFirst: "Create first project",
    filterEmpty: "No projects in this category.",
    processingHint: "Usually ready within 1–5 minutes.",
    openProject: "Open project",
  },
  np: {
    stages: ["Project", "Photos", "Generate"],
    nameLabel: "Project name",
    namePlaceholder: "For example: Apartment in Ghent, Villa Knokke or Project 2026-08",
    formatLabel: "Format",
    portrait: "Portrait", portraitDesc: "1080 × 1920 · Best for Instagram, TikTok and Reels",
    landscape: "Landscape", landscapeDesc: "1920 × 1080 · Best for websites, YouTube and presentations",
    ratioNote: "Portrait and landscape are separate projects and each uses one project credit.",
    photosTitle: "Upload your property photos",
    photosSub: "Add up to 20 photos and place them in the order you want them to appear.",
    dropText: "Click or drag photos here",
    dropHint: "JPEG, PNG or WebP · max 20 MB · up to {max} photos",
    selected: "{n} / {max} photos selected",
    photoAlt: "Photo {n}",
    replaceTitle: "Replace",
    summaryPhotos: "Photos",
    summaryResult: "Result",
    resultValue: "6 videos · MP4",
    resultHint: "Six finished videos · MP4 · 30 FPS · {res}.",
    noCredits: "You have no project credits available.",
    choosePackage: "Choose a package",
    generate: "Generate videos",
    continueBtn: "Continue",
    back: "Back",
    confirmTitle: "Generate this project?",
    confirmText: "This uses 1 project credit and creates six videos in your selected format.",
    confirmGenerate: "Confirm and generate",
    toastMax: "Maximum of {max} photos reached",
    toastType: "{name}: unsupported type",
    toastSize: "{name}: larger than 20 MB",
    toastInvalid: "Invalid file.",
    preparing: "Preparing project…",
    uploading: "Uploading photo {i} of {n}",
    starting: "Starting video generation…",
    started: "Project started. Your videos are being created.",
    uploadFailed: "Photo {i} failed to upload. Your credit was not used. {msg}",
  },
  bill: {
    title: "Plans & credits",
    sub: "Choose the package that best fits your agency and manage your project credits.",
    notices: {
      past_due: { title: "Payment needs attention", text: "We couldn't process your subscription payment. Update your payment method to continue creating new projects.", portal: "Update payment method" },
      unpaid: { title: "Subscription unpaid", text: "Your subscription is no longer active because payment could not be completed. Choose a plan below to start again.", portal: "Manage billing" },
      incomplete: { title: "Subscription setup incomplete", text: "Your subscription payment has not been completed yet.", portal: "Complete payment" },
      incomplete_expired: { title: "Subscription not activated", text: "The subscription setup was not completed. Choose a plan below to try again." },
      paused: { title: "Subscription paused", text: "Your subscription is currently paused.", portal: "Manage billing" },
      cancelled: { title: "Subscription ended", text: "Your subscription is no longer active. Choose a plan below to reactivate — your existing projects and videos remain available." },
    },
    openingPortal: "Opening billing portal…",
    creditsLabel: "Available credits",
    projectCredits: "project credits",
    complimentary: "Complimentary credit — try Real Estate Pro on us",
    creditExplainer: "One project credit creates six videos in one selected format.",
    currentPlan: "Current plan",
    pendingPlan: "Pending plan",
    noPlan: "No active plan",
    creditsAfterPayment: "Your credits are added only after payment has been confirmed.",
    active: "Active",
    monthlyPrice: "Monthly price",
    perMonthVat: "/ month, excl. VAT",
    monthlyCredits: "Monthly project credits",
    nextBilling: "Next billing date",
    billingInfo: "Billing information",
    vatTaxId: "VAT / Tax ID:",
    cancelScheduled: "Your subscription is scheduled to end on {date}. You keep full access until then, and you can resume it anytime from Manage subscription.",
    manageSubscription: "Manage subscription",
    viewWebsite: "View website",
    backToDashboard: "Back to dashboard",
    otherPlans: "Other plans",
    chooseYourPlan: "Choose your plan",
    switchHelpAdmin: "You can switch plans below — changes take effect **immediately** and your existing project credits are always kept. To cancel or update payment details, use **Manage subscription** above.",
    switchHelpUser: "Only an agency administrator can change the plan. To cancel or update payment details, an administrator can use **Manage subscription** above.",
    viewOnly: "Only an agency administrator can start or manage a subscription.",
    acceptTerms: "I agree to the {l:terms} and acknowledge the {l:privacy}.",
    confirmBusiness: "I confirm that I am purchasing Real Estate Pro for professional or business purposes.",
    openingCheckout: "Opening secure checkout…",
    switchTo: "Switch to {name}",
    choose: "Choose {name}",
    switchTitle: "Switch to {name}",
    switchBody1: "You are switching from **{current}** to **{target}**. Your {target} subscription will start immediately.",
    switchBody2: "You will be charged **€{price} excl. VAT** now, plus any applicable tax.",
    switchBody3: "Your current unused paid credits will remain in your account. After the new payment succeeds, **{credits} new project credits** will be added to your balance.",
    switchBody4: "Your new {target} subscription will renew monthly from today. Any remaining time in your current {current} billing period is not prorated or refunded.",
    switchResume: "Your subscription is currently scheduled for cancellation. Switching plans will **resume** your subscription and start the selected new plan immediately.",
    confirmPay: "Confirm and pay €{price}",
    processing: "Processing…",
    successTitle: "Plan changed successfully",
    successText: "Your {plan} plan is now active. {credits} new project credits have been added to your balance. Your existing credits were preserved. You now have **{total} project credits**. Your next billing date is **{date}**.",
    processingTitle: "Finalising your plan change",
    processingText: "Your payment is being confirmed. Your new plan and credits will appear here shortly once the payment is complete. Your existing credits remain safe.",
    errorTitle: "We couldn't complete your plan change",
    howTitle: "How credits work",
    howText: "Each successful monthly subscription payment adds your plan's project credits to your balance. Unused paid credits roll over and do not expire while your account remains open and Real Estate Pro continues to offer the Service. Cancelling stops future monthly credit grants but does not remove paid credits already in your balance. If you change plans, your existing credits remain and the new plan allocation is added after the next successful renewal payment. Complimentary credits follow their own separate validity rules. See our {l:terms|credit & subscription terms}.",
    futureNote: "You'll be redirected to secure checkout to complete your subscription. Prices are shown excl. VAT; applicable tax is calculated at checkout. Your credits are added only after payment has been confirmed.",
    errAccept: "Please accept the Terms and confirm your business purchase to continue.",
    errActiveSub: "You already have an active subscription. Use Manage subscription to make changes.",
    errCheckout: "We couldn't open secure checkout right now. Please try again in a moment.",
    errPortal: "We couldn't open the billing portal right now. Please try again in a moment.",
    errSwitch409: "A plan change is already being processed. Please wait a moment and check your billing details.",
    errSwitchFailed: "We couldn't complete your plan change. No new plan credits were added and your existing credits remain safe. Please try again or update your payment method.",
  },
  acct: {
    title: "Account", personal: "Personal details", organisation: "Organisation",
    email: "Email", role: "Role", administrator: "Administrator", user: "User",
    billing: "Billing", password: "Password",
  },
  support: { needHelp: "Need help or have a question?", contact: "Contact support" },
  legalUi: {
    back: "Back to website", updated: "Last updated: August 2026",
    opBy: "Operated by", entNo: "Enterprise number", vat: "VAT",
  },
  titles: {
    login: "Log in", register: "Create account", changePassword: "Change password",
    dashboard: "Dashboard", newProject: "New project", account: "Account",
    billing: "Plans & credits", checkoutCancel: "Checkout cancelled",
    checkoutSuccess: "Confirming payment", project: "Project",
  },
  seo: {
    terms: { title: "Terms & Conditions — Real Estate Pro", description: "Read the terms governing the use of Real Estate Pro: subscriptions, credits, payments and acceptable use." },
    privacy: { title: "Privacy Policy — Real Estate Pro", description: "How Real Estate Pro processes personal data: what data, purposes, legal bases and your rights." },
    cookies: { title: "Cookie Policy — Real Estate Pro", description: "Real Estate Pro only uses strictly necessary cookies for authentication and security. Read the cookie policy." },
    legal: { title: "Legal Notice — Real Estate Pro", description: "Company and contact details of Real Estate Pro." },
    dpa: { title: "Data Processing Addendum — Real Estate Pro", description: "The data processing addendum between Real Estate Pro and business customers for personal data." },
  },
  legal: LEGAL.en,
};

/* ----------------------------- FRENCH ----------------------------- */
const fr = {
  common: { replay: "Revoir la vidéo", language: "Langue" },
  nav: { home: "Accueil", ariaHome: "Real Estate Pro — vers la page d'accueil", ariaMenuOpen: "Ouvrir le menu", ariaMenuClose: "Fermer le menu" },
  auth: {
    register: {
      errShort: "Le mot de passe doit contenir au moins 8 caractères.",
      errMismatch: "Les mots de passe ne correspondent pas.",
      errAccept: "Veuillez accepter les Conditions générales et la Politique de confidentialité.",
      confirm: "Confirmer",
    },
    changePassword: { short: "Le nouveau mot de passe doit contenir au moins 8 caractères." },
  },
  app: {
    checkout: {
      readyTitle: "Votre compte est prêt",
      activatedPlan: "Forfait activé",
      availableCredits: "Crédits de projet disponibles",
      createFirst: "Créez votre premier projet",
      toDashboard: "Aller au tableau de bord",
      almostThere: "Presque terminé",
      confirming: "Confirmation de votre paiement…",
      slowText: "Votre paiement a été reçu et est en cours de confirmation. Cette page se mettra à jour automatiquement.",
      confirmingText: "Veuillez patienter pendant que nous confirmons votre paiement et activons vos crédits de projet.",
      cancelTitle: "Paiement annulé",
      cancelText: "Aucun paiement n'a été effectué et rien n'a changé sur votre compte. Vous pouvez choisir un forfait et réessayer quand vous le souhaitez.",
      tryAgain: "Réessayer",
      backToDashboard: "Retour au tableau de bord",
    },
  },
  dash: {
    welcomeTitle: "Créez des vidéos immobilières en quelques minutes",
    welcomeSub: "Importez vos photos de biens et recevez six vidéos immobilières prêtes à l'emploi.",
    newProject: "Nouveau projet",
    creditsLabel: "Crédits disponibles",
    projectCredits: "crédits de projet",
    complimentary: "Crédit offert — essayez Real Estate Pro à nos frais",
    creditExplainer: "Un crédit de projet permet de créer six vidéos dans un format choisi.",
    attn: {
      past_due: { title: "Paiement à vérifier", text: "Nous n'avons pas pu traiter le paiement de votre abonnement : aucun nouveau crédit mensuel n'a été ajouté. Les crédits de projet déjà présents dans votre solde restent disponibles. Mettez à jour votre moyen de paiement pour rétablir vos crédits mensuels.", cta: "Mettre à jour le moyen de paiement" },
      unpaid: { title: "Abonnement impayé", text: "Votre abonnement n'est plus actif car le paiement n'a pas pu être effectué. Les crédits de projet déjà présents dans votre solde restent disponibles. Gérez votre facturation pour rétablir les crédits mensuels.", cta: "Gérer la facturation" },
      incomplete: { title: "Configuration de l'abonnement incomplète", text: "Le paiement de votre abonnement n'est pas encore terminé. Finalisez le paiement pour activer votre forfait et vos crédits mensuels.", cta: "Finaliser le paiement" },
      incomplete_expired: { title: "Abonnement non activé", text: "La configuration de l'abonnement n'a pas été terminée. Choisissez un forfait pour réessayer. Votre compte, vos projets et vos crédits existants restent disponibles.", cta: "Voir les forfaits" },
      paused: { title: "Abonnement suspendu", text: "Votre abonnement est actuellement suspendu. Les crédits de projet déjà présents dans votre solde restent disponibles. Gérez votre facturation pour rétablir les crédits mensuels.", cta: "Gérer la facturation" },
    },
    viewBilling: "Voir la facturation",
    cancelTitle: "Résiliation programmée",
    cancelText: "Votre abonnement prendra fin le {date}. Vous pouvez continuer à utiliser Real Estate Pro jusque-là.",
    manageSubscription: "Gérer l'abonnement",
    endedTitle: "Votre abonnement est terminé",
    endedText: "Il vous reste {n} {cw} de projet payé(s) disponible(s). Vous pouvez continuer à créer des projets avec ces crédits. Choisissez un nouveau forfait quand vous souhaitez recevoir de nouveaux crédits mensuels.",
    cw1: "crédit", cwN: "crédits",
    viewPlans: "Voir les forfaits",
    zeroTitleMore: "Prêt à créer davantage ?",
    zeroTitleStart: "Choisissez un forfait pour commencer à créer des vidéos",
    zeroTextEnded: "Choisissez un forfait pour recevoir de nouveaux crédits de projet — votre compte, vos projets et vos vidéos restent ici.",
    zeroTextDemo: "Vous avez utilisé votre crédit offert. Choisissez un forfait pour recevoir de nouveaux crédits de projet — votre compte, vos projets et vos vidéos restent ici.",
    zeroTextNew: "Sélectionnez un forfait Real Estate Pro pour activer des crédits de projet sur votre compte.",
    projectsHeading: "Projets",
    filters: { all: "Tous", CONCEPT: "Brouillons", PROCESSING: "En traitement", COMPLETED: "Terminés", SUPPORT_REVIEW: "En contrôle" },
    states: { CONCEPT: "Brouillon", PROCESSING: "Génération en cours", COMPLETED: "Vidéos prêtes", SUPPORT_REVIEW: "En contrôle" },
    noProjects: "Aucun projet pour le moment",
    emptyCanCreate: "Créez votre premier projet et transformez vos photos immobilières en six vidéos finalisées.",
    emptyNoCredits: "Choisissez un forfait ci-dessus pour activer vos crédits et commencer à créer des vidéos.",
    createFirst: "Créer un premier projet",
    filterEmpty: "Aucun projet dans cette catégorie.",
    processingHint: "Généralement prêt en 1 à 5 minutes.",
    openProject: "Ouvrir le projet",
  },
  np: {
    stages: ["Projet", "Photos", "Générer"],
    nameLabel: "Nom du projet",
    namePlaceholder: "Par exemple : Appartement à Gand, Villa Knokke ou Projet 2026-08",
    formatLabel: "Format",
    portrait: "Vertical", portraitDesc: "1080 × 1920 · Idéal pour Instagram, TikTok et les Reels",
    landscape: "Horizontal", landscapeDesc: "1920 × 1080 · Idéal pour les sites web, YouTube et les présentations",
    ratioNote: "Le vertical et l'horizontal sont des projets distincts et utilisent chacun un crédit de projet.",
    photosTitle: "Importez vos photos immobilières",
    photosSub: "Ajoutez jusqu'à 20 photos et placez-les dans l'ordre souhaité.",
    dropText: "Cliquez ou déposez vos photos ici",
    dropHint: "JPEG, PNG ou WebP · 20 Mo max · jusqu'à {max} photos",
    selected: "{n} / {max} photos sélectionnées",
    photoAlt: "Photo {n}",
    replaceTitle: "Remplacer",
    summaryPhotos: "Photos",
    summaryResult: "Résultat",
    resultValue: "6 vidéos · MP4",
    resultHint: "Six vidéos finalisées · MP4 · 30 FPS · {res}.",
    noCredits: "Vous n'avez aucun crédit de projet disponible.",
    choosePackage: "Choisir un forfait",
    generate: "Générer les vidéos",
    continueBtn: "Continuer",
    back: "Retour",
    confirmTitle: "Générer ce projet ?",
    confirmText: "Cela utilise 1 crédit de projet et crée six vidéos dans le format choisi.",
    confirmGenerate: "Confirmer et générer",
    toastMax: "Maximum de {max} photos atteint",
    toastType: "{name} : type non pris en charge",
    toastSize: "{name} : plus de 20 Mo",
    toastInvalid: "Fichier invalide.",
    preparing: "Préparation du projet…",
    uploading: "Import de la photo {i} sur {n}",
    starting: "Démarrage de la génération vidéo…",
    started: "Projet démarré. Vos vidéos sont en cours de création.",
    uploadFailed: "La photo {i} n'a pas pu être importée. Votre crédit n'a pas été utilisé. {msg}",
  },
  bill: {
    title: "Forfaits & crédits",
    sub: "Choisissez le forfait le mieux adapté à votre agence et gérez vos crédits de projet.",
    notices: {
      past_due: { title: "Paiement à vérifier", text: "Nous n'avons pas pu traiter le paiement de votre abonnement. Mettez à jour votre moyen de paiement pour continuer à créer de nouveaux projets.", portal: "Mettre à jour le moyen de paiement" },
      unpaid: { title: "Abonnement impayé", text: "Votre abonnement n'est plus actif car le paiement n'a pas pu être effectué. Choisissez un forfait ci-dessous pour recommencer.", portal: "Gérer la facturation" },
      incomplete: { title: "Configuration de l'abonnement incomplète", text: "Le paiement de votre abonnement n'est pas encore terminé.", portal: "Finaliser le paiement" },
      incomplete_expired: { title: "Abonnement non activé", text: "La configuration de l'abonnement n'a pas été terminée. Choisissez un forfait ci-dessous pour réessayer." },
      paused: { title: "Abonnement suspendu", text: "Votre abonnement est actuellement suspendu.", portal: "Gérer la facturation" },
      cancelled: { title: "Abonnement résilié", text: "Votre abonnement n'est plus actif. Choisissez un forfait ci-dessous pour le réactiver — vos projets et vidéos existants restent disponibles." },
    },
    openingPortal: "Ouverture du portail de facturation…",
    creditsLabel: "Crédits disponibles",
    projectCredits: "crédits de projet",
    complimentary: "Crédit offert — essayez Real Estate Pro à nos frais",
    creditExplainer: "Un crédit de projet permet de créer six vidéos dans un format choisi.",
    currentPlan: "Forfait actuel",
    pendingPlan: "Forfait en attente",
    noPlan: "Aucun forfait actif",
    creditsAfterPayment: "Vos crédits ne sont ajoutés qu'une fois le paiement confirmé.",
    active: "Actif",
    monthlyPrice: "Prix mensuel",
    perMonthVat: "/ mois, hors TVA",
    monthlyCredits: "Crédits de projet mensuels",
    nextBilling: "Prochaine date de facturation",
    billingInfo: "Informations de facturation",
    vatTaxId: "N° de TVA :",
    cancelScheduled: "Votre abonnement prendra fin le {date}. Vous conservez un accès complet jusque-là et vous pouvez le réactiver à tout moment depuis Gérer l'abonnement.",
    manageSubscription: "Gérer l'abonnement",
    viewWebsite: "Voir le site web",
    backToDashboard: "Retour au tableau de bord",
    otherPlans: "Autres forfaits",
    chooseYourPlan: "Choisissez votre forfait",
    switchHelpAdmin: "Vous pouvez changer de forfait ci-dessous — les changements prennent effet **immédiatement** et vos crédits de projet existants sont toujours conservés. Pour résilier ou mettre à jour vos coordonnées de paiement, utilisez **Gérer l'abonnement** ci-dessus.",
    switchHelpUser: "Seul un administrateur de l'agence peut changer de forfait. Pour résilier ou mettre à jour les coordonnées de paiement, un administrateur peut utiliser **Gérer l'abonnement** ci-dessus.",
    viewOnly: "Seul un administrateur de l'agence peut démarrer ou gérer un abonnement.",
    acceptTerms: "J'accepte les {l:terms} et je reconnais avoir pris connaissance de la {l:privacy}.",
    confirmBusiness: "Je confirme que j'achète Real Estate Pro à des fins professionnelles ou commerciales.",
    openingCheckout: "Ouverture du paiement sécurisé…",
    switchTo: "Passer à {name}",
    choose: "Choisir {name}",
    switchTitle: "Passer à {name}",
    switchBody1: "Vous passez de **{current}** à **{target}**. Votre abonnement {target} démarre immédiatement.",
    switchBody2: "Vous serez facturé de **{price} € hors TVA** maintenant, plus toute taxe applicable.",
    switchBody3: "Vos crédits payés non utilisés actuels restent sur votre compte. Une fois le nouveau paiement réussi, **{credits} nouveaux crédits de projet** seront ajoutés à votre solde.",
    switchBody4: "Votre nouvel abonnement {target} sera renouvelé mensuellement à partir d'aujourd'hui. Le temps restant de votre période de facturation {current} actuelle n'est ni calculé au prorata ni remboursé.",
    switchResume: "Votre abonnement est actuellement programmé pour résiliation. Changer de forfait **réactivera** votre abonnement et démarrera immédiatement le nouveau forfait sélectionné.",
    confirmPay: "Confirmer et payer {price} €",
    processing: "Traitement…",
    successTitle: "Forfait modifié avec succès",
    successText: "Votre forfait {plan} est maintenant actif. {credits} nouveaux crédits de projet ont été ajoutés à votre solde. Vos crédits existants ont été conservés. Vous disposez maintenant de **{total} crédits de projet**. Votre prochaine date de facturation est le **{date}**.",
    processingTitle: "Finalisation de votre changement de forfait",
    processingText: "Votre paiement est en cours de confirmation. Votre nouveau forfait et vos crédits apparaîtront ici dès que le paiement sera terminé. Vos crédits existants restent en sécurité.",
    errorTitle: "Nous n'avons pas pu finaliser votre changement de forfait",
    howTitle: "Comment fonctionnent les crédits",
    howText: "Chaque paiement mensuel réussi ajoute les crédits de projet de votre forfait à votre solde. Les crédits payés non utilisés sont reportés et n'expirent pas tant que votre compte reste ouvert et que Real Estate Pro continue de proposer le service. La résiliation met fin aux crédits mensuels futurs mais ne supprime pas les crédits payés déjà présents dans votre solde. Si vous changez de forfait, vos crédits existants sont conservés et la nouvelle allocation est ajoutée après le prochain paiement de renouvellement réussi. Les crédits offerts suivent leurs propres règles de validité. Consultez nos {l:terms|conditions relatives aux crédits et abonnements}.",
    futureNote: "Vous serez redirigé vers le paiement sécurisé pour finaliser votre abonnement. Les prix sont affichés hors TVA ; la taxe applicable est calculée au moment du paiement. Vos crédits ne sont ajoutés qu'une fois le paiement confirmé.",
    errAccept: "Veuillez accepter les Conditions et confirmer votre achat professionnel pour continuer.",
    errActiveSub: "Vous avez déjà un abonnement actif. Utilisez Gérer l'abonnement pour apporter des modifications.",
    errCheckout: "Impossible d'ouvrir le paiement sécurisé pour le moment. Veuillez réessayer dans un instant.",
    errPortal: "Impossible d'ouvrir le portail de facturation pour le moment. Veuillez réessayer dans un instant.",
    errSwitch409: "Un changement de forfait est déjà en cours de traitement. Patientez un instant et vérifiez vos informations de facturation.",
    errSwitchFailed: "Nous n'avons pas pu finaliser votre changement de forfait. Aucun nouveau crédit n'a été ajouté et vos crédits existants restent en sécurité. Veuillez réessayer ou mettre à jour votre moyen de paiement.",
  },
  acct: {
    title: "Compte", personal: "Informations personnelles", organisation: "Organisation",
    email: "E-mail", role: "Rôle", administrator: "Administrateur", user: "Utilisateur",
    billing: "Facturation", password: "Mot de passe",
  },
  support: { needHelp: "Besoin d'aide ou une question ?", contact: "Contacter le support" },
  legalUi: {
    back: "Retour au site", updated: "Dernière mise à jour : août 2026",
    opBy: "Exploité par", entNo: "Numéro d'entreprise", vat: "TVA",
  },
  titles: {
    login: "Connexion", register: "Créer un compte", changePassword: "Modifier le mot de passe",
    dashboard: "Tableau de bord", newProject: "Nouveau projet", account: "Compte",
    billing: "Forfaits & crédits", checkoutCancel: "Paiement annulé",
    checkoutSuccess: "Confirmation du paiement", project: "Projet",
  },
  seo: {
    terms: { title: "Conditions générales — Real Estate Pro", description: "Consultez les conditions régissant l'utilisation de Real Estate Pro : abonnements, crédits, paiements et règles d'utilisation." },
    privacy: { title: "Politique de confidentialité — Real Estate Pro", description: "Comment Real Estate Pro traite les données personnelles : quelles données, quelles finalités, quelles bases légales et quels droits." },
    cookies: { title: "Politique en matière de cookies — Real Estate Pro", description: "Real Estate Pro n'utilise que des cookies strictement nécessaires à l'authentification et à la sécurité. Lisez la politique en matière de cookies." },
    legal: { title: "Mentions légales — Real Estate Pro", description: "Coordonnées et informations de l'entreprise Real Estate Pro." },
    dpa: { title: "Addendum de traitement des données — Real Estate Pro", description: "L'addendum de traitement des données entre Real Estate Pro et ses clients professionnels concernant les données personnelles." },
  },
  legal: LEGAL.fr,
};

/* ----------------------------- SPANISH ---------------------------- */
const es = {
  common: { replay: "Ver el vídeo de nuevo", language: "Idioma" },
  nav: { home: "Inicio", ariaHome: "Real Estate Pro — a la página de inicio", ariaMenuOpen: "Abrir menú", ariaMenuClose: "Cerrar menú" },
  auth: {
    register: {
      errShort: "La contraseña debe tener al menos 8 caracteres.",
      errMismatch: "Las contraseñas no coinciden.",
      errAccept: "Acepta los Términos y la Política de privacidad.",
      confirm: "Confirmar",
    },
    changePassword: { short: "La nueva contraseña debe tener al menos 8 caracteres." },
  },
  app: {
    checkout: {
      readyTitle: "Tu cuenta está lista",
      activatedPlan: "Plan activado",
      availableCredits: "Créditos de proyecto disponibles",
      createFirst: "Crea tu primer proyecto",
      toDashboard: "Ir al panel",
      almostThere: "Casi listo",
      confirming: "Confirmando tu pago…",
      slowText: "Tu pago se ha recibido y aún se está confirmando. Esta página se actualizará automáticamente.",
      confirmingText: "Espera un momento mientras confirmamos tu pago y activamos tus créditos de proyecto.",
      cancelTitle: "Pago cancelado",
      cancelText: "No se ha realizado ningún pago y nada ha cambiado en tu cuenta. Puedes elegir un plan e intentarlo de nuevo cuando quieras.",
      tryAgain: "Intentarlo de nuevo",
      backToDashboard: "Volver al panel",
    },
  },
  dash: {
    welcomeTitle: "Crea vídeos inmobiliarios en minutos",
    welcomeSub: "Sube las fotos de tu inmueble y recibe seis vídeos inmobiliarios listos para usar.",
    newProject: "Nuevo proyecto",
    creditsLabel: "Créditos disponibles",
    projectCredits: "créditos de proyecto",
    complimentary: "Crédito de cortesía — prueba Real Estate Pro por nuestra cuenta",
    creditExplainer: "Un crédito de proyecto crea seis vídeos en un formato seleccionado.",
    attn: {
      past_due: { title: "El pago necesita atención", text: "No pudimos procesar el pago de tu suscripción, por lo que no se añadieron nuevos créditos mensuales. Los créditos de proyecto que ya tienes en tu saldo siguen disponibles. Actualiza tu método de pago para reanudar tus créditos mensuales.", cta: "Actualizar método de pago" },
      unpaid: { title: "Suscripción impagada", text: "Tu suscripción ya no está activa porque no se pudo completar el pago. Los créditos de proyecto que ya tienes en tu saldo siguen disponibles. Gestiona tu facturación para reanudar los créditos mensuales.", cta: "Gestionar facturación" },
      incomplete: { title: "Configuración de suscripción incompleta", text: "El pago de tu suscripción aún no se ha completado. Completa el pago para activar tu plan y tus créditos mensuales.", cta: "Completar pago" },
      incomplete_expired: { title: "Suscripción no activada", text: "La configuración de la suscripción no se completó. Elige un plan para intentarlo de nuevo. Tu cuenta, tus proyectos y tus créditos existentes siguen disponibles.", cta: "Ver planes" },
      paused: { title: "Suscripción en pausa", text: "Tu suscripción está en pausa actualmente. Los créditos de proyecto que ya tienes en tu saldo siguen disponibles. Gestiona tu facturación para reanudar los créditos mensuales.", cta: "Gestionar facturación" },
    },
    viewBilling: "Ver facturación",
    cancelTitle: "Cancelación programada",
    cancelText: "Tu suscripción terminará el {date}. Puedes seguir usando Real Estate Pro hasta entonces.",
    manageSubscription: "Gestionar suscripción",
    endedTitle: "Tu suscripción ha terminado",
    endedText: "Aún tienes {n} {cw} de proyecto pagado(s) disponible(s). Puedes seguir creando proyectos con estos créditos. Elige un nuevo plan cuando quieras recibir nuevos créditos mensuales.",
    cw1: "crédito", cwN: "créditos",
    viewPlans: "Ver planes",
    zeroTitleMore: "¿Listo para crear más?",
    zeroTitleStart: "Elige un plan para empezar a crear vídeos",
    zeroTextEnded: "Elige un plan para recibir nuevos créditos de proyecto — tu cuenta, tus proyectos y tus vídeos seguirán aquí.",
    zeroTextDemo: "Has usado tu crédito de cortesía. Elige un plan para recibir nuevos créditos de proyecto — tu cuenta, tus proyectos y tus vídeos seguirán aquí.",
    zeroTextNew: "Selecciona un plan de Real Estate Pro para activar créditos de proyecto en tu cuenta.",
    projectsHeading: "Proyectos",
    filters: { all: "Todos", CONCEPT: "Borradores", PROCESSING: "En proceso", COMPLETED: "Completados", SUPPORT_REVIEW: "En revisión" },
    states: { CONCEPT: "Borrador", PROCESSING: "Generando", COMPLETED: "Vídeos listos", SUPPORT_REVIEW: "En revisión" },
    noProjects: "Aún no hay proyectos",
    emptyCanCreate: "Crea tu primer proyecto y convierte tus fotos inmobiliarias en seis vídeos terminados.",
    emptyNoCredits: "Elige un plan arriba para activar tus créditos y empezar a crear vídeos.",
    createFirst: "Crear el primer proyecto",
    filterEmpty: "No hay proyectos en esta categoría.",
    processingHint: "Normalmente listo en 1–5 minutos.",
    openProject: "Abrir proyecto",
  },
  np: {
    stages: ["Proyecto", "Fotos", "Generar"],
    nameLabel: "Nombre del proyecto",
    namePlaceholder: "Por ejemplo: Apartamento en Gante, Villa Knokke o Proyecto 2026-08",
    formatLabel: "Formato",
    portrait: "Vertical", portraitDesc: "1080 × 1920 · Ideal para Instagram, TikTok y Reels",
    landscape: "Horizontal", landscapeDesc: "1920 × 1080 · Ideal para webs, YouTube y presentaciones",
    ratioNote: "El formato vertical y el horizontal son proyectos independientes y cada uno utiliza un crédito de proyecto.",
    photosTitle: "Sube tus fotos inmobiliarias",
    photosSub: "Añade hasta 20 fotos y ordénalas como quieras que aparezcan.",
    dropText: "Haz clic o arrastra las fotos aquí",
    dropHint: "JPEG, PNG o WebP · máx. 20 MB · hasta {max} fotos",
    selected: "{n} / {max} fotos seleccionadas",
    photoAlt: "Foto {n}",
    replaceTitle: "Reemplazar",
    summaryPhotos: "Fotos",
    summaryResult: "Resultado",
    resultValue: "6 vídeos · MP4",
    resultHint: "Seis vídeos terminados · MP4 · 30 FPS · {res}.",
    noCredits: "No tienes créditos de proyecto disponibles.",
    choosePackage: "Elegir un plan",
    generate: "Generar vídeos",
    continueBtn: "Continuar",
    back: "Atrás",
    confirmTitle: "¿Generar este proyecto?",
    confirmText: "Esto utiliza 1 crédito de proyecto y crea seis vídeos en el formato seleccionado.",
    confirmGenerate: "Confirmar y generar",
    toastMax: "Se alcanzó el máximo de {max} fotos",
    toastType: "{name}: tipo no admitido",
    toastSize: "{name}: supera los 20 MB",
    toastInvalid: "Archivo no válido.",
    preparing: "Preparando el proyecto…",
    uploading: "Subiendo foto {i} de {n}",
    starting: "Iniciando la generación de vídeo…",
    started: "Proyecto iniciado. Tus vídeos se están creando.",
    uploadFailed: "No se pudo subir la foto {i}. No se ha utilizado tu crédito. {msg}",
  },
  bill: {
    title: "Planes y créditos",
    sub: "Elige el plan que mejor se adapte a tu agencia y gestiona tus créditos de proyecto.",
    notices: {
      past_due: { title: "El pago necesita atención", text: "No pudimos procesar el pago de tu suscripción. Actualiza tu método de pago para seguir creando nuevos proyectos.", portal: "Actualizar método de pago" },
      unpaid: { title: "Suscripción impagada", text: "Tu suscripción ya no está activa porque no se pudo completar el pago. Elige un plan abajo para empezar de nuevo.", portal: "Gestionar facturación" },
      incomplete: { title: "Configuración de suscripción incompleta", text: "El pago de tu suscripción aún no se ha completado.", portal: "Completar pago" },
      incomplete_expired: { title: "Suscripción no activada", text: "La configuración de la suscripción no se completó. Elige un plan abajo para intentarlo de nuevo." },
      paused: { title: "Suscripción en pausa", text: "Tu suscripción está en pausa actualmente.", portal: "Gestionar facturación" },
      cancelled: { title: "Suscripción finalizada", text: "Tu suscripción ya no está activa. Elige un plan abajo para reactivarla — tus proyectos y vídeos existentes siguen disponibles." },
    },
    openingPortal: "Abriendo el portal de facturación…",
    creditsLabel: "Créditos disponibles",
    projectCredits: "créditos de proyecto",
    complimentary: "Crédito de cortesía — prueba Real Estate Pro por nuestra cuenta",
    creditExplainer: "Un crédito de proyecto crea seis vídeos en un formato seleccionado.",
    currentPlan: "Plan actual",
    pendingPlan: "Plan pendiente",
    noPlan: "Sin plan activo",
    creditsAfterPayment: "Tus créditos se añaden solo después de que se confirme el pago.",
    active: "Activo",
    monthlyPrice: "Precio mensual",
    perMonthVat: "/ mes, sin IVA",
    monthlyCredits: "Créditos de proyecto mensuales",
    nextBilling: "Próxima fecha de facturación",
    billingInfo: "Información de facturación",
    vatTaxId: "NIF / IVA:",
    cancelScheduled: "Tu suscripción terminará el {date}. Mantienes el acceso completo hasta entonces y puedes reanudarla en cualquier momento desde Gestionar suscripción.",
    manageSubscription: "Gestionar suscripción",
    viewWebsite: "Ver sitio web",
    backToDashboard: "Volver al panel",
    otherPlans: "Otros planes",
    chooseYourPlan: "Elige tu plan",
    switchHelpAdmin: "Puedes cambiar de plan abajo — los cambios se aplican **inmediatamente** y tus créditos de proyecto existentes siempre se conservan. Para cancelar o actualizar los datos de pago, usa **Gestionar suscripción** arriba.",
    switchHelpUser: "Solo un administrador de la agencia puede cambiar el plan. Para cancelar o actualizar los datos de pago, un administrador puede usar **Gestionar suscripción** arriba.",
    viewOnly: "Solo un administrador de la agencia puede iniciar o gestionar una suscripción.",
    acceptTerms: "Acepto los {l:terms} y reconozco la {l:privacy}.",
    confirmBusiness: "Confirmo que compro Real Estate Pro con fines profesionales o comerciales.",
    openingCheckout: "Abriendo el pago seguro…",
    switchTo: "Cambiar a {name}",
    choose: "Elegir {name}",
    switchTitle: "Cambiar a {name}",
    switchBody1: "Estás cambiando de **{current}** a **{target}**. Tu suscripción {target} comenzará inmediatamente.",
    switchBody2: "Se te cobrará **{price} € sin IVA** ahora, más los impuestos aplicables.",
    switchBody3: "Tus créditos pagados no utilizados permanecerán en tu cuenta. Una vez que el nuevo pago se complete, se añadirán **{credits} nuevos créditos de proyecto** a tu saldo.",
    switchBody4: "Tu nueva suscripción {target} se renovará mensualmente a partir de hoy. El tiempo restante de tu periodo de facturación actual de {current} no se prorratea ni se reembolsa.",
    switchResume: "Tu suscripción está programada para cancelarse. Al cambiar de plan se **reanudará** tu suscripción y comenzará el nuevo plan seleccionado inmediatamente.",
    confirmPay: "Confirmar y pagar {price} €",
    processing: "Procesando…",
    successTitle: "Plan cambiado correctamente",
    successText: "Tu plan {plan} ya está activo. Se han añadido {credits} nuevos créditos de proyecto a tu saldo. Tus créditos existentes se han conservado. Ahora tienes **{total} créditos de proyecto**. Tu próxima fecha de facturación es el **{date}**.",
    processingTitle: "Finalizando tu cambio de plan",
    processingText: "Tu pago se está confirmando. Tu nuevo plan y tus créditos aparecerán aquí en cuanto se complete el pago. Tus créditos existentes están a salvo.",
    errorTitle: "No pudimos completar tu cambio de plan",
    howTitle: "Cómo funcionan los créditos",
    howText: "Cada pago mensual correcto de la suscripción añade los créditos de proyecto de tu plan a tu saldo. Los créditos pagados no utilizados se acumulan y no caducan mientras tu cuenta siga abierta y Real Estate Pro siga ofreciendo el servicio. Cancelar detiene las futuras asignaciones mensuales de créditos, pero no elimina los créditos pagados que ya están en tu saldo. Si cambias de plan, tus créditos existentes se conservan y la nueva asignación se añade tras el próximo pago de renovación correcto. Los créditos de cortesía siguen sus propias reglas de validez. Consulta nuestros {l:terms|términos de créditos y suscripción}.",
    futureNote: "Serás redirigido al pago seguro para completar tu suscripción. Los precios se muestran sin IVA; el impuesto aplicable se calcula en el momento del pago. Tus créditos se añaden solo después de que se confirme el pago.",
    errAccept: "Acepta los Términos y confirma tu compra comercial para continuar.",
    errActiveSub: "Ya tienes una suscripción activa. Usa Gestionar suscripción para hacer cambios.",
    errCheckout: "No pudimos abrir el pago seguro en este momento. Inténtalo de nuevo en un momento.",
    errPortal: "No pudimos abrir el portal de facturación en este momento. Inténtalo de nuevo en un momento.",
    errSwitch409: "Ya se está procesando un cambio de plan. Espera un momento y revisa tus datos de facturación.",
    errSwitchFailed: "No pudimos completar tu cambio de plan. No se añadieron nuevos créditos y tus créditos existentes están a salvo. Inténtalo de nuevo o actualiza tu método de pago.",
  },
  acct: {
    title: "Cuenta", personal: "Datos personales", organisation: "Organización",
    email: "Correo electrónico", role: "Rol", administrator: "Administrador", user: "Usuario",
    billing: "Facturación", password: "Contraseña",
  },
  support: { needHelp: "¿Necesitas ayuda o tienes una pregunta?", contact: "Contactar con soporte" },
  legalUi: {
    back: "Volver al sitio web", updated: "Última actualización: agosto de 2026",
    opBy: "Operado por", entNo: "Número de empresa", vat: "IVA",
  },
  titles: {
    login: "Iniciar sesión", register: "Crear cuenta", changePassword: "Cambiar contraseña",
    dashboard: "Panel", newProject: "Nuevo proyecto", account: "Cuenta",
    billing: "Planes y créditos", checkoutCancel: "Pago cancelado",
    checkoutSuccess: "Confirmando el pago", project: "Proyecto",
  },
  seo: {
    terms: { title: "Términos y condiciones — Real Estate Pro", description: "Lee los términos que rigen el uso de Real Estate Pro: suscripciones, créditos, pagos y reglas de uso." },
    privacy: { title: "Política de privacidad — Real Estate Pro", description: "Cómo trata Real Estate Pro los datos personales: qué datos, con qué fines, bases jurídicas y tus derechos." },
    cookies: { title: "Política de cookies — Real Estate Pro", description: "Real Estate Pro solo utiliza cookies estrictamente necesarias para la autenticación y la seguridad. Lee la política de cookies." },
    legal: { title: "Aviso legal — Real Estate Pro", description: "Datos de la empresa y de contacto de Real Estate Pro." },
    dpa: { title: "Anexo de tratamiento de datos — Real Estate Pro", description: "El anexo de tratamiento de datos entre Real Estate Pro y los clientes empresariales sobre datos personales." },
  },
  legal: LEGAL.es,
};

export const EXTRA = { nl, en, fr, es };
